import type { CheckoutProductType } from '../product_type.js';
import type { CheckoutField } from '../product_checkout_schema.js';

/** Per-product checkout field manifests. Each product picks its subset of the
 *  shared corporate/traveler atoms; the drawer selects one via `fieldsFor`
 *  (design spec §"Wire carries the superset"). */

// ── Shared atoms (defined once, composed into the product arrays) ────────────
const allocation: CheckoutField = { kind: 'allocation', group: 'corporate' };
const birthdate: CheckoutField = { kind: 'birthdate', group: 'traveler' };
const nationality: CheckoutField = { kind: 'nationality', group: 'traveler' };
const motive: CheckoutField = {
  kind: 'select',
  group: 'corporate',
  id: 'motive',
  labelKey: 'productCheckout.fieldMotive',
};
const justification: CheckoutField = {
  kind: 'select',
  group: 'corporate',
  id: 'justification',
  labelKey: 'productCheckout.fieldJustification',
};
const observation: CheckoutField = { kind: 'observation', group: 'corporate', maxLength: 300 };
const phone: CheckoutField = { kind: 'phone', group: 'traveler' };

/** Rental default identity form: allocation + full traveler identity
 *  (birthdate + nationality + document with all three doc types) + corporate
 *  motive/justification/observation. */
export const RENTAL_FIELDS: CheckoutField[] = [
  allocation,
  birthdate,
  nationality,
  { kind: 'document', group: 'traveler', allow: ['cpf', 'passport', 'dni'] },
  motive,
  justification,
  observation,
];

/** Hotel minimal form: allocation + corporate motive/justification/observation,
 *  NENHUM campo de identidade. No birthdate and NO document: o upstream
 *  de hotel aceita apenas `{travelerId, firstName, lastName}` (`hotel/booking.ts`),
 *  e o antigo campo CPF-only read-only bloqueava permanentemente qualquer
 *  perfil sem CPF em ficha — sem alternativa de tipo e sem caminho self-service
 *  para cadastrar o CPF (issue #2309). Sem o campo no manifesto, `canSubmit` e
 *  `checkout_validation` ignoram o flag `document` do servidor para hotel.
 *
 *  Sem `nationality` também (decisão de 17/10/2026, #5279): o hotel NÃO consome
 *  `edits` em nenhum caminho (`hotel/booking.ts` e
 *  `domains/hotel/graph/nodes/checkout.ts` montam o hóspede só com o nome do
 *  PERFIL), então o campo era pedido, exigido pelo `canSubmit` e descartado sem
 *  erro. Se o contrato upstream abrir slot para ela, o campo volta. */
export const HOTEL_FIELDS: CheckoutField[] = [allocation, motive, justification, observation];

/** Flight identity superset (design spec §1): the rental identity set PLUS the
 *  general `gender` field and the flight-only `embark` (boarding name + surname)
 *  and required `phone`. Order mirrors the Flutter `flight_motive_form.dart`
 *  render order (embark/document/personal/gender) adapted to the unified layout:
 *  identity chips first, then the complete document, then corporate. */
const FLIGHT_FIELDS: CheckoutField[] = [
  allocation,
  birthdate,
  nationality,
  { kind: 'gender', group: 'traveler' },
  { kind: 'embark', group: 'traveler' }, // boarding name + surname
  phone, // required for flight
  { kind: 'document', group: 'traveler', allow: ['cpf', 'passport', 'dni'] },
  motive,
  justification,
  observation,
];

/** Bus is a mobility/transport product but its Flutter checkout uses the default
 *  `TravelInfoField` (document + birthdate + nationality only) — NO gender/embark/
 *  phone. It stays aliased to the rental identity set. */
const BUS_FIELDS: CheckoutField[] = RENTAL_FIELDS;

/** Mobility (rides/car-with-driver) checkout: allocation + phone (the driver
 *  contact) + corporate motive/justification/observation. A justificativa só
 *  aparece fora da política (`offerFieldsFor`): hoje só a cooperativa que passa
 *  do limite de distância; o carro com motorista vem sempre dentro (#5330). Segue
 *  no `approval.justification` do agendamento. No traveler identity fields
 *  (birthdate/nationality/document).
 *
 *  `phone` é OBRIGATÓRIO (o `phoneOk` do `canSubmit` gateia pelo manifesto) e é
 *  o número que vale na corrida: `domains/mobility/graph/nodes/checkout_submit.ts`
 *  lê o `edits.phone` do bloco do viajante (sem ele, o submit é recusado antes de
 *  queimar o desafio) e `domains/mobility/logic/create_schedule_input.ts` o manda
 *  como `phoneNumber` do agendamento. */
export const MOBILITY_FIELDS: CheckoutField[] = [
  allocation,
  phone,
  motive,
  justification,
  observation,
];

/** O pedido OFFLINE à equipe VOLL (`concierge_checkout`): rateio + motivo/
 *  justificativa/observação, e NENHUM campo de identidade — o legado manda só
 *  `{id, name, email, costCenters}` por viajante (`conciergeFlightBooking`,
 *  `hotelBooking` concierge) e a equipe emite com o perfil quando a cotação
 *  for aprovada. Pedir documento/telefone aqui seria coletar o que ninguém lê. */
export const CONCIERGE_FIELDS: CheckoutField[] = [allocation, motive, justification, observation];

/** O pedido OFFLINE de ÔNIBUS: o legado (`voll_bus`) manda o viajante COMPLETO,
 *  como na reserva (`busCreateBooking` com documento e nascimento), então o
 *  form pede a identidade da reserva de ônibus. Sem justificativa: o app a
 *  esconde no pedido (não há viagem fora da política para justificar). */
export const BUS_CONCIERGE_FIELDS: CheckoutField[] = BUS_FIELDS.filter(
  (field) => field !== justification,
);

/** O manifesto de cada produto — a metade de campos do contrato zero-React
 *  (a outra é `core/product_contract.ts`); a tela de cada produto mora em
 *  `products/<produto>/`. O tipo mapeado obriga um produto novo a declarar o seu. */
const FIELDS_BY_PRODUCT: { readonly [P in CheckoutProductType]: CheckoutField[] } = {
  rental: RENTAL_FIELDS,
  hotel: HOTEL_FIELDS,
  flight: FLIGHT_FIELDS,
  bus: BUS_FIELDS,
  mobility: MOBILITY_FIELDS,
};

/** Selects the field manifest for a product. */
export function fieldsFor(productType: CheckoutProductType): CheckoutField[] {
  return FIELDS_BY_PRODUCT[productType];
}

/** O manifesto do PEDIDO à equipe VOLL de um produto — o que a tela do pedido
 *  mostra e o que o BFF cobra no envio (#7251): a régua é uma só, senão o
 *  servidor recusa por um campo que o formulário não tem. */
export function conciergeFieldsFor(productType: CheckoutProductType): CheckoutField[] {
  return productType === 'bus' ? BUS_CONCIERGE_FIELDS : CONCIERGE_FIELDS;
}

/** O manifesto da COMPRA de uma oferta: dentro da política não há o que
 *  justificar, então a justificativa sai da tela e do envio — como no app
 *  (`flight_motive_form.dart` só mostra o campo fora da política). O pedido à
 *  equipe (`CONCIERGE_FIELDS`) segue com a justificativa opcional. */
export function offerFieldsFor(
  productType: CheckoutProductType,
  withinPolicy: boolean,
): CheckoutField[] {
  const fields = fieldsFor(productType);
  return withinPolicy ? fields.filter((field) => field !== justification) : fields;
}
